import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Divider from '@mui/material/Divider';
import type { MenuProps } from '@mui/material/Menu';
import Menu, { menuClasses } from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import { useDimensions } from '@trinity/chat-components/hooks';
import { ThemeGroupContext, ThemeGroupSelectContext } from '../contexts';
import { SelectOption } from './SelectOption';
import styles from './Select.module.css';

const StyledMenu = styled(Menu, {
  shouldForwardProp: prop => prop !== 'width',
})<MenuProps & { width: number }>(({ width }) => ({
  [`& .${menuClasses.paper}`]: {
    borderRadius: '4px',
    transitionDuration: '0',
    width,
  },
  [`& .${menuClasses.list}`]: {
    padding: '0',
  },
}));

type Props = {
  children: React.ReactNode;
  /** When true the menu cannot be opened */
  disabled?: boolean;
};

export const SelectInput = (props: Props): React.JSX.Element => {
  const ctx = useContext(ThemeGroupContext);
  const dimensions = useDimensions();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!ctx) {
    throw new Error('SelectInput must be used within ThemeGroupContext');
  }

  const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (props.disabled) return;
    setAnchor(e.currentTarget);
  }, [props.disabled]);

  const handleClose = useCallback(() => {
    setAnchor(null);
  }, []);

  useEffect(() => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth);
    }
  }, [dimensions]);

  const selected = useMemo(() => {
    return ctx.themes.find(x => x.id === ctx.selected);
  }, [ctx]);

  const context = useMemo(() => ({
    visible: !!anchor,
    close: handleClose,
  }), [anchor, handleClose]);

  return (
    <ThemeGroupSelectContext.Provider value={context}>
      <div className={styles.root}>
        <div
          aria-disabled={props.disabled || undefined}
          className={styles.btn}
          onClick={handleClick}
          ref={ref}
          style={props.disabled ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}>
          <SelectOption
            readonly
            item={selected} />
          <div className={styles.indicator}>
            <Divider
              sx={{ marginLeft: '4px', marginRight: '4px', height: '28px' }}
              orientation="vertical"
              variant="middle" />
            <KeyboardArrowDown sx={{ height: '25px', width: '25px', color: 'var(--trolley)' }} />
          </div>
        </div>
        <StyledMenu
          width={width}
          anchorEl={anchor}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          className={styles.root}
          disableAutoFocusItem
          onClose={handleClose}
          open={!!anchor}
          transitionDuration={{ appear: 0, enter: 0, exit: 0 }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
          {props.children}
        </StyledMenu>
      </div>
    </ThemeGroupSelectContext.Provider>
  );
};

SelectInput.displayName = 'ChartTheming.Select.Input';
